// INVARIANT: sign-in is atomic and durable. Once /v1/auth/google returns a token
// the user stays signed in; sync failures only report via syncStatus and must
// never clear `user`. Only /v1/auth/me confirming a dead token ends a session
// (handleUnauthorized).

import {
  signInWithGoogle,
  fetchMe,
  logoutRequest,
  deleteAccountRequest,
  pushSync,
  pullSync,
  getStoredAuth,
  setStoredAuth
} from '../../services/sync';
import {
  getUnsynced,
  getUnsyncedDayDeltas,
  markSynced,
  mergeRemote,
  remoteResumePoint,
  repairSyncedDayTotalsOnce,
  getSyncState,
  setSyncState,
  archiveHistoryForUser
} from '../../services/history';

export const SYNC_IDLE = 'idle';
export const SYNC_SYNCING = 'syncing';
export const SYNC_ERROR = 'error';

// The Worker rejects bigger pushes (413) rather than dropping rows.
const PUSH_CHUNK = 200;

// Push and pull never overlap: a pull landing between a push's request and its
// markSynced would count that push twice in syncedPracticeSeconds (BUGFIX-008).
let syncQueue = Promise.resolve();
function serialized(fn) {
  const run = syncQueue.then(fn, fn);
  syncQueue = run.catch(function() {});
  return run;
}

async function doPush({ commit, dispatch }) {
  const stored = getStoredAuth();
  if (!stored) return;
  repairSyncedDayTotalsOnce();
  const unsynced = getUnsynced();
  let dayDeltas = getUnsyncedDayDeltas();
  if (!unsynced.length && !dayDeltas.length) return;

  commit('SET_SYNC_STATUS', SYNC_SYNCING);
  // Day deltas ride on the first chunk only.
  for (let i = 0; i === 0 || i < unsynced.length; i += PUSH_CHUNK) {
    const chunk = unsynced.slice(i, i + PUSH_CHUNK);
    let result;
    try {
      result = await pushSync(stored.token, chunk, dayDeltas);
    } catch (_) {
      commit('SET_SYNC_STATUS', SYNC_ERROR);
      return;
    }
    if (result.unauthorized) {
      await dispatch('handleUnauthorized');
      return;
    }
    markSynced(chunk.map(function(s) { return s.id; }), result.at, dayDeltas);
    dayDeltas = [];
  }
  commit('SET_SYNC_STATUS', SYNC_IDLE);
}

async function doPull({ commit, dispatch }) {
  const stored = getStoredAuth();
  if (!stored) return;
  repairSyncedDayTotalsOnce();
  const syncState = getSyncState();
  // No cursor = first sign-in here or account switch.
  const fullPull = !syncState.lastPulledAt;
  let cursor = syncState.lastPulledAt;
  let point = null;

  commit('SET_SYNC_STATUS', SYNC_SYNCING);
  for (;;) {
    let pullResult;
    try {
      pullResult = await pullSync(stored.token, cursor);
    } catch (_) {
      commit('SET_SYNC_STATUS', SYNC_ERROR);
      return;
    }
    if (pullResult.unauthorized) {
      await dispatch('handleUnauthorized');
      return;
    }
    // Must run before mergeRemote: it spots remote rows by ids not yet in local history.
    const pagePoint = remoteResumePoint(pullResult.sessions);
    if (pagePoint && (!point || pagePoint.at > point.at)) point = pagePoint;
    // Always merge: day totals change without new sessions (listening time).
    mergeRemote(pullResult.sessions, pullResult.dayTotals);
    cursor = pullResult.pulledAt;
    setSyncState({ userId: syncState.userId, lastPulledAt: cursor });
    if (!pullResult.more) break;
  }

  if (point) commit('SET_RESUME_POINT', Object.assign({ fullPull: fullPull }, point));
  dispatch('stats/refreshStats', null, { root: true });
  commit('SET_SYNC_STATUS', SYNC_IDLE);
}


export default {
  namespaced: true,

  state: {
    user: null,        // { id, email, name, picture } or null when signed out
    loading: false,    // a sign-in round trip is in flight
    error: null,       // sign-in failure, shown next to the Google button
    syncStatus: SYNC_IDLE,
    // Position from another device found by a pull; LearningScreen decides whether to act (STORY-005).
    resumePoint: null
  },

  getters: {
    isSignedIn: state => !!state.user
  },

  mutations: {
    SET_USER(state, user) { state.user = user; },
    SET_LOADING(state, v) { state.loading = v; },
    SET_ERROR(state, msg) { state.error = msg; },
    SET_SYNC_STATUS(state, status) { state.syncStatus = status; },
    SET_RESUME_POINT(state, point) { state.resumePoint = point; }
  },

  actions: {
    // A 401 here is authoritative (stored token is dead), so it does sign out.
    async restoreSession({ commit, dispatch }) {
      const stored = getStoredAuth();
      if (!stored || !stored.token) return;
      try {
        const result = await fetchMe(stored.token);
        if (!result) { dispatch('endSession'); return; }
        commit('SET_USER', result.user);
        dispatch('pull').catch(function() {});
      } catch (_) {
        // Offline must not sign anyone out.
      }
    },

    async signIn({ commit, dispatch }, credential) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      let result;
      try {
        result = await signInWithGoogle(credential);
      } catch (e) {
        commit('SET_ERROR', (e && e.message) || 'Sign in failed');
        commit('SET_LOADING', false);
        return false;
      }

      // Identity is final here; nothing below may sign the user out.
      setStoredAuth({ token: result.token, expiresAt: result.expiresAt });
      commit('SET_USER', result.user);
      commit('SET_LOADING', false);
      if (window.$analytics) window.$analytics.trackEvent('login', { method: 'Google' });

      dispatch('syncAfterSignIn', result.user.id).catch(function() {});
      return true;
    },

    // Fire-and-forget from signIn: reports through syncStatus, never through `user`.
    async syncAfterSignIn({ dispatch, commit }, userId) {
      const syncState = getSyncState();

      if (!syncState.userId) {
        // First sign-in on this device: adopt local history into the account.
        await dispatch('push');
        setSyncState({ userId: userId, lastPulledAt: null });
        await dispatch('pull');
        return;
      }

      if (syncState.userId !== userId) {
        // Different account: park the previous account's history instead of merging.
        archiveHistoryForUser(syncState.userId);
        setSyncState({ userId: userId, lastPulledAt: null });
        await dispatch('pull');
        commit('SET_SYNC_STATUS', SYNC_IDLE);
        dispatch('stats/refreshStats', null, { root: true });
        return;
      }

      await dispatch('push');
      await dispatch('pull');
    },

    push(context) { return serialized(() => doPush(context)); },

    pull(context) { return serialized(() => doPull(context)); },

    // A sync 401 is not trusted on its own: only end the session if /v1/auth/me
    // also rejects the token. Otherwise stay signed in with a sync error.
    async handleUnauthorized({ commit, dispatch }) {
      commit('SET_SYNC_STATUS', SYNC_ERROR);
      const stored = getStoredAuth();
      if (!stored || !stored.token) return;
      try {
        const me = await fetchMe(stored.token);
        if (me === null) dispatch('endSession');
      } catch (_) {
      }
    },

    // The only path that clears `user`.
    endSession({ commit }) {
      setStoredAuth(null);
      commit('SET_USER', null);
      commit('SET_SYNC_STATUS', SYNC_IDLE);
    },

    async logout({ commit, dispatch }) {
      const stored = getStoredAuth();
      if (stored) logoutRequest(stored.token);
      dispatch('endSession');
      commit('SET_ERROR', null);
      // Keep local history and syncState.userId so re-signing into the same account resumes.
    },

    async deleteAccount({ dispatch }) {
      const stored = getStoredAuth();
      if (stored) await deleteAccountRequest(stored.token);
      dispatch('endSession');
      setSyncState({ userId: null, lastPulledAt: null });
    }
  }
};
